import { Chapter, Sequence } from './NavigationSlice';

/*
  A chapter the learner tested out of in the pretest stays in the outline for
  review, but its slides are not part of the learner's path: the server skips
  them for progress, Next and completion. These helpers answer the questions
  the player asks about that path. With no pretest every slide is required, so
  they behave exactly like the plain course order.
*/

type Item = Sequence | Chapter;

const isSequence = (item: Item): item is Sequence => item.kind === 'sequence';

const testedOutChapterIds = (items: Item[]) =>
  new Set(
    items
      .filter(i => i.kind === 'chapter' && i.testedOut)
      .map(chapter => chapter.id)
  );

export const isInTestedOutChapter = (items: Item[], sequence: Sequence) =>
  testedOutChapterIds(items).has(sequence.chapterId);

const requiredSequences = (items: Item[]) => {
  const testedOut = testedOutChapterIds(items);
  return items.filter(
    (i): i is Sequence => isSequence(i) && !testedOut.has(i.chapterId)
  );
};

export const firstRequiredSequence = (items: Item[]) =>
  requiredSequences(items)[0] as Sequence | undefined;

/**
 * The furthest unlocked slide on the learner's path, where they pick the
 * course back up. Falls back to the first required slide.
 */
export const resumeSequence = (
  items: Item[],
  highestUnlockedPosition: number
) => {
  const required = requiredSequences(items);
  const unlocked = required.filter(i => i.position <= highestUnlockedPosition);
  return (unlocked[unlocked.length - 1] ?? required[0]) as Sequence | undefined;
};

export const nextSequenceInChapter = (items: Item[], sequence: Sequence) =>
  items.find(
    (i): i is Sequence =>
      isSequence(i) &&
      i.chapterId === sequence.chapterId &&
      i.position > sequence.position
  );

/**
 * Where Next leads. From a slide on the learner's path it is the next slide on
 * that path, as the server answers; while the learner reviews a chapter they
 * tested out of, it is the next slide of that chapter and then their resume
 * point.
 */
export const nextPathSequence = (
  items: Item[],
  sequence: Sequence,
  highestUnlockedPosition: number
) => {
  if (isInTestedOutChapter(items, sequence)) {
    return (
      nextSequenceInChapter(items, sequence) ??
      resumeSequence(items, highestUnlockedPosition)
    );
  }

  return requiredSequences(items).find(i => i.position > sequence.position);
};

/**
 * Where Back goes. From a slide on the learner's path it skips the chapters
 * they tested out of; while they review one of those, it steps through it.
 */
export const previousSequence = (items: Item[], sequence: Sequence) => {
  const testedOut = testedOutChapterIds(items);
  const reviewing = testedOut.has(sequence.chapterId);
  const earlier = items.filter(
    (i): i is Sequence =>
      isSequence(i) &&
      i.position < sequence.position &&
      (reviewing || !testedOut.has(i.chapterId))
  );
  return earlier[earlier.length - 1] as Sequence | undefined;
};

/**
 * Flags exactly the given chapters as tested out. Returns the same array when
 * nothing changes and only replaces the chapters that do, so slides keep their
 * identity and the player does not re-render them.
 */
export const withTestedOutChapters = (
  items: Item[],
  chapterIds: Iterable<string>
) => {
  const testedOut = new Set(chapterIds);
  let changed = false;

  const next = items.map(item => {
    if (item.kind !== 'chapter') return item;

    const isTestedOut = testedOut.has(item.id);
    if (!!item.testedOut === isTestedOut) return item;

    changed = true;
    return { ...item, testedOut: isTestedOut };
  });

  return changed ? next : items;
};
